import { createSignal, createEffect, createMemo, onCleanup, For, Show, untrack } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { A, useParams } from '@solidjs/router';
import Text from '../components/Text';
import Modal from '../components/Modal';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useActiveBot } from '../state/activeBot';
import { useToast } from '../components/MessageBox';
import { showConfirm, showDialog } from '../components/Dialog';
import { useItemName } from '../hooks/useItemName';
import { useTranslation } from '../hooks/useTranslation';
import { useCopy } from '../hooks/useCopy';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { useInventoryApi, type StorageInfo } from '../services/inventoryApi';
import { formatQuantity } from '../services/quantityFormatter';
import { getEnchantmentName, ENCHANTMENT_IDS } from '../services/enchantmentNames';
import { resolveItemDisplayName } from '../services/itemDisplayName';
import { cachedNameOf, createSellbotNameCache, matchItemsByRelevance, searchSellbotItems } from '../services/sellbotItemSearch';
import ItemStackView from '../components/ItemStackView';
import CurrencyInput from '../components/CurrencyInput';
import QuantityInput from '../components/QuantityInput';
import StorageSelect from '../components/StorageSelect';
import SellbotLimitsEditor from '../components/SellbotLimitsEditor';
import SellbotPausedBadge from '../components/SellbotPausedBadge';
import { maxDurability } from '../services/itemDurability';
import { useDebounced } from '../hooks/useDebounced';
import { subscribeBotEngineEvents } from '../services/botEngineEvents';
import { debounce } from '../services/debounceCallback';
import { parseSnbt } from '../services/snbtReader';
import type { ItemStack } from '../models/itemStack';
import type { ItemDef } from '../services/craftingResolver';
import type { SellbotOfferDto, ItemMatchRuleDto, ItemMatchRuleRequest, SellbotDimensionDto, ItemStackDto, AnkaufPriceStageRequest, SellbotBundleDto, InventoryCustomItemDto, SellbotPriceTierDto, SellbotTierRequest, SellbotWizardRequest } from '../api';
import styles from './Sellbot.module.css';
import ItemIdentityEditor from '../components/ItemIdentityEditor';
import ItemIdentityWizard, { type WizardItemSource } from '../components/ItemIdentityWizard';

const NUMERIC_OPS = ['Lt', 'Le', 'Eq', 'Ge', 'Gt'] as const;
const ANKAUF_DESTINATION_OVERRIDES = ['', 'SellChamber', 'LagerChest'] as const;


export default function SellbotOffer() {
    const params = useParams();
    const api = useApi();
    const inv = useInventoryApi();
    const toast = useToast();
    const { t } = useTranslation();
    const copy = useCopy();
    const resolver = useCraftingResolver();
    const { itemName } = useItemName();
    const { botId } = useActiveBot();

    const [offerStore, setOfferStore] = createStore<{ offer: SellbotOfferDto | null }>({ offer: null });
    const offer = () => offerStore.offer;
    const setOffer = (v: SellbotOfferDto | null) => setOfferStore('offer', v ? reconcile(v, { key: 'id' }) : null);

    // Bundles are config-wide: the collision check needs every price in the shop, not just this offer's.
    const [bundles, setBundles] = createStore<SellbotBundleDto[]>([]);
    // The whole shop's offers, not names derived from them: a name resolved at load time would be the
    // raw item key, because the crafting resolver is still loading then.
    const [shopOffers, setShopOffers] = createStore<SellbotOfferDto[]>([]);
    const [bundleNameDrafts, setBundleNameDrafts] = createStore<Record<string, string>>({});
    const [loading, setLoading] = createSignal(true);
    const [tCount, setTCount] = createSignal<number | ''>(1);
    const [tPrice, setTPrice] = createSignal<number | ''>('');

    const [stageCount, setStageCount] = createSignal<number | ''>(1);
    const [stagePrice, setStagePrice] = createSignal<number | ''>('');

    const [noteDraft, setNoteDraft] = createSignal<string | null>(null);
    const [editingTierId, setEditingTierId] = createSignal<string | null>(null);
    const [editTierCount, setEditTierCount] = createSignal<number | ''>('');
    const [editTierPrice, setEditTierPrice] = createSignal<number | ''>('');

    // Keyed by dimension, not by rule: a null draft is a pending "no rule", which no rule id could carry.
    const [ruleDrafts, setRuleDrafts] = createStore<Record<string, { rule: ItemMatchRuleRequest | null } | undefined>>({});

    const [editingStageId, setEditingStageId] = createSignal<string | null>(null);
    const [editStageCount, setEditStageCount] = createSignal<number | ''>('');
    const [editStagePrice, setEditStagePrice] = createSignal<number | ''>('');

    const enableAnkauf = async () => {
        const id = botId(); const o = offer();
        if (!id || !o) return;
        const res = await api.sellbot.upsertAnkaufTier(id, o.id, { isEnabled: true });
        if (isOk(res.data)) await load();
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const deleteAnkaufTier = async () => {
        const id = botId(); const o = offer();
        if (!id || !o) return;
        if (!await showConfirm(t('SELLBOT_CONFIRM_DELETE_ANKAUF_TIER'), true, { confirmLabel: t('COMMON_DELETE'), cancelLabel: t('COMMON_CANCEL') })) return;
        const res = await api.sellbot.deleteAnkaufTier(id, o.id);
        if (isOk(res.data)) await load();
        else toast?.error(t('SELLBOT_TOAST_DELETE_FAILED'));
    };

    const toggleAnkaufTier = async () => {
        const id = botId(); const o = offer();
        if (!id || !o || !o.ankaufTier) return;
        const res = await api.sellbot.upsertAnkaufTier(id, o.id, {
            isEnabled: !o.ankaufTier.isEnabled, destinationOverride: o.ankaufTier.destinationOverride ?? null,
        });
        if (isOk(res.data)) await load();
        else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const changeAnkaufDestinationOverride = async (value: string) => {
        const id = botId(); const o = offer();
        if (!id || !o || !o.ankaufTier) return;
        const res = await api.sellbot.upsertAnkaufTier(id, o.id, {
            isEnabled: o.ankaufTier.isEnabled, destinationOverride: value === '' ? null : value,
        });
        if (isOk(res.data)) await load();
        else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const addStage = async () => {
        const id = botId(); const o = offer();
        if (!id || !o || !o.ankaufTier) return;
        const priceVal = Number(stagePrice() || 0);
        const countVal = Number(stageCount() || 0);
        if (priceVal <= 0 || countVal <= 0) { toast?.error(t('SELLBOT_TOAST_PRICE_REQUIRED')); return; }
        const body: AnkaufPriceStageRequest = { count: countVal, price: priceVal, isEnabled: true };
        const res = await api.sellbot.createAnkaufPriceStage(id, o.ankaufTier.id, body);
        if (isOk(res.data)) { setStagePrice(''); setStageCount(1); await load(); }
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_OFFER_ADD_FAILED'));
    };

    const toggleStage = async (stageId: string, isEnabled: boolean, count: number, price: number) => {
        const id = botId();
        if (!id) return;
        const res = await api.sellbot.updateAnkaufPriceStage(id, stageId, { count, price, isEnabled: !isEnabled });
        if (isOk(res.data)) await load();
        else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const startEditStage = (stageId: string, count: number, price: number) => {
        setEditStageCount(count); setEditStagePrice(price); setEditingStageId(stageId);
    };
    const cancelEditStage = () => setEditingStageId(null);
    const saveEditStage = async (stageId: string, isEnabled: boolean) => {
        const id = botId();
        if (!id) return;
        const priceVal = Number(editStagePrice() || 0);
        const countVal = Number(editStageCount() || 0);
        if (priceVal <= 0 || countVal <= 0) { toast?.error(t('SELLBOT_TOAST_PRICE_REQUIRED')); return; }
        const res = await api.sellbot.updateAnkaufPriceStage(id, stageId, { count: countVal, price: priceVal, isEnabled });
        if (isOk(res.data)) { setEditingStageId(null); await load(); }
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const deleteStage = async (stageId: string) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('SELLBOT_CONFIRM_DELETE_ANKAUF_STAGE'), true, { confirmLabel: t('COMMON_DELETE'), cancelLabel: t('COMMON_CANCEL') })) return;
        const res = await api.sellbot.deleteAnkaufPriceStage(id, stageId);
        if (isOk(res.data)) await load();
        else toast?.error(t('SELLBOT_TOAST_DELETE_FAILED'));
    };

    const [pickerOpen, setPickerOpen] = createSignal(false);
    const [storages, setStorages] = createStore<StorageInfo[]>([]);
    const [pickStorageId, setPickStorageId] = createSignal('');
    const [pickItems, setPickItems] = createStore<ItemStackDto[]>([]);
    const [pickQuery, setPickQuery] = createSignal('');
    const [wizardOpen, setWizardOpen] = createSignal(false);
    const [wizardStock, setWizardStock] = createStore<ItemStackDto[]>([]);
    const [wizardQuantities, setWizardQuantities] = createStore<Record<string, number>>({});
    const [wizardCustomItems, setWizardCustomItems] = createStore<InventoryCustomItemDto[]>([]);
    // Both pickers on this page name items the same way, so one cache serves them.
    const itemNameCache = createSellbotNameCache();

    const displayName = (item: ItemStackDto): string =>
        resolveItemDisplayName({ itemId: item.itemId, damage: item.metadata, snbt: item.snbt ?? undefined }, resolver());

    const itemVariants = createMemo(() => {
        const o = offer(); const r = resolver();
        return o && r ? r.getVariantsById(o.item.itemId) : [];
    });

    const variantLabel = (variant: ItemDef): string =>
        `${itemName(variant.key)} (${variant.metadata})`;

    const itemDurabilityMax = (): number | undefined => {
        const o = offer();
        return o ? maxDurability(resolver()?.getItemById(o.item.itemId, 0)?.name) : undefined;
    };
    const stackSizeOf = (item: { itemId: number; metadata: number }) =>
        resolver()?.getItemById(item.itemId, item.metadata)?.stackSize;
    const offerStackSize = () => {
        const o = offer();
        return o ? stackSizeOf(o.item) : undefined;
    };
    const itemTagCount = () => parseSnbt(offer()?.item.snbt ?? undefined).tagCount ?? 0;

    const fmtMoney = (n: number) => `${new Intl.NumberFormat('de-DE').format(n)}$`;
    const toView = (s: ItemStackDto) => ({ itemId: s.itemId, count: s.count, damage: s.metadata, snbt: s.snbt ?? undefined });

    const load = async () => {
        const id = botId();
        if (!id) { setOffer(null); setLoading(false); return; }
        const res = await api.sellbot.getConfig(id);
        if (!isOk(res.data)) { setOffer(null); setLoading(false); return; }

        const config = res.data.value;
        setOffer(config.offers.find(o => o.id === params.offerId) ?? null);
        setBundles(reconcile(config.bundles ?? [], { key: 'price' }));
        setShopOffers(reconcile(config.offers, { key: 'id' }));
        setLoading(false);
    };

    createEffect(() => {
        const id = botId();
        const oid = params.offerId;
        setLoading(true);
        untrack(() => { void load(); });
        void oid;
        if (!id) return;
        const reloadOnEvent = debounce(() => void load(), 500);
        const unsub = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type === 'plugin_event' && evt.payload?.source === 'sellbot') reloadOnEvent();
        });
        onCleanup(() => { reloadOnEvent.cancel(); unsub(); });
    });

    const bundleAt = (price: number) => bundles.find(b => b.price === price);

    const siblingName = (offerId: string): string => {
        const sibling = shopOffers.find(o => o.id === offerId);
        return sibling ? displayName(sibling.item) : t('SELLBOT_BUNDLE_THIS_ITEM');
    };

    const siblingStackSize = (offerId: string) => {
        const sibling = shopOffers.find(o => o.id === offerId);
        return sibling ? stackSizeOf(sibling.item) : offerStackSize();
    };

    // Sharing a price makes the items drop together, which is a different product from either of them.
    // Returns the name to send, '' to send none, or null when the user backed out.
    const confirmBundle = async (price: number, excludeTierOwnerId: string | null): Promise<string | null> => {
        const others = shopOffers
            .filter(sibling => sibling.id !== excludeTierOwnerId && sibling.tiers.some(tier => tier.price === price))
            .map(sibling => displayName(sibling.item));
        if (others.length === 0) return '';

        const otherNames = [...new Set(others)].join(', ');
        const existing = bundleAt(price);
        if (existing?.name) {
            const joined = await showConfirm(
                t('SELLBOT_BUNDLE_JOIN_TITLE', existing.name) + ' ' + t('SELLBOT_BUNDLE_JOIN_BODY', otherNames, fmtMoney(price)),
                false, { confirmLabel: t('SELLBOT_BUNDLE_JOIN_CONFIRM'), cancelLabel: t('COMMON_CANCEL') });
            return joined ? '' : null;
        }

        const answer = await showDialog({
            title: t('SELLBOT_BUNDLE_CONFIRM_TITLE', otherNames, fmtMoney(price)),
            description: t('SELLBOT_BUNDLE_CONFIRM_BODY', otherNames, fmtMoney(price)),
            fields: [{ key: 'name', label: t('SELLBOT_BUNDLE_NAME'), defaultValue: '', type: 'text', placeholder: t('SELLBOT_BUNDLE_NAME_PLACEHOLDER') }],
            confirmLabel: t('SELLBOT_BUNDLE_CONFIRM_CREATE'),
            cancelLabel: t('COMMON_CANCEL'),
        });
        if (answer === null) return null;
        const name = (answer.name ?? '').trim();
        if (!name) { toast?.error(t('SELLBOT_TOAST_BUNDLE_NAME_REQUIRED')); return null; }
        return name;
    };

    const addTier = async () => {
        const id = botId(); const o = offer();
        if (!id || !o) return;
        const priceVal = Number(tPrice() || 0);
        const countVal = Number(tCount() || 0);
        if (priceVal <= 0 || countVal <= 0) { toast?.error(t('SELLBOT_TOAST_PRICE_REQUIRED')); return; }
        // A bundle delivers each item once, so a second tier on this offer at the same price has no
        // meaning. Said here rather than letting the server reject it after the bundle dialog.
        if (o.tiers.some(tier => tier.price === priceVal)) { toast?.error(t('SELLBOT_TOAST_ITEM_PRICE_TAKEN')); return; }
        const bundleName = await confirmBundle(priceVal, o.id);
        if (bundleName === null) return;
        const res = await api.sellbot.createTier(id, o.id, { count: countVal, price: priceVal, isEnabled: true, isUnlisted: false, bundleName: bundleName || undefined });
        if (isOk(res.data)) { setTPrice(''); setTCount(1); await load(); }
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_OFFER_ADD_FAILED'));
    };

    const saveNote = async () => {
        const id = botId(); const o = offer(); const draft = noteDraft();
        if (!id || !o || draft === null) return;
        const res = await api.sellbot.updateOfferNote(id, o.id, { note: draft });
        if (isOk(res.data)) { setNoteDraft(null); toast?.info(t('SELLBOT_TOAST_SAVED')); await load(); }
        else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const setIgnoresStockCache = async (ignoresStockCache: boolean) => {
        const id = botId(); const o = offer();
        if (!id || !o) return;
        const res = await api.sellbot.updateOfferStockCache(id, o.id, { ignoresStockCache });
        if (isOk(res.data)) { toast?.info(t('SELLBOT_TOAST_SAVED')); await load(); }
        else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const setListAsCompressed = async (choice: string) => {
        const id = botId(); const o = offer();
        if (!id || !o) return;
        const listAsCompressed = choice === '' ? null : choice === 'on';
        const res = await api.sellbot.updateOfferCompressedListing(id, o.id, { listAsCompressed });
        if (isOk(res.data)) { toast?.info(t('SELLBOT_TOAST_SAVED')); await load(); }
        else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const setUnlisted = async (isUnlisted: boolean) => {
        const id = botId(); const o = offer();
        if (!id || !o) return;
        const res = await api.sellbot.updateOfferUnlisted(id, o.id, { isUnlisted });
        if (isOk(res.data)) { toast?.info(t('SELLBOT_TOAST_SAVED')); await load(); }
        else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    // The update is a whole-tier PUT, so every field it does not mean to change is carried over from
    // the row rather than re-sent from a shorter argument list that would silently reset the rest.
    const patchTier = async (tier: SellbotPriceTierDto, patch: Partial<SellbotTierRequest>) => {
        const id = botId();
        if (!id) return;
        const res = await api.sellbot.updateTier(id, tier.id, {
            count: tier.count, price: tier.price, isEnabled: tier.isEnabled, isUnlisted: tier.isUnlisted, ...patch,
        });
        if (isOk(res.data)) await load();
        else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const startEditTier = (tierId: string, count: number, price: number) => {
        setEditTierCount(count); setEditTierPrice(price); setEditingTierId(tierId);
    };
    const cancelEditTier = () => setEditingTierId(null);
    const saveEditTier = async (tierId: string, isEnabled: boolean, isUnlisted: boolean) => {
        const id = botId();
        if (!id) return;
        const priceVal = Number(editTierPrice() || 0);
        const countVal = Number(editTierCount() || 0);
        if (priceVal <= 0 || countVal <= 0) { toast?.error(t('SELLBOT_TOAST_PRICE_REQUIRED')); return; }
        const o = offer();
        const bundleName = await confirmBundle(priceVal, o?.id ?? null);
        if (bundleName === null) return;
        const res = await api.sellbot.updateTier(id, tierId, { count: countVal, price: priceVal, isEnabled, isUnlisted, bundleName: bundleName || undefined });
        if (isOk(res.data)) { setEditingTierId(null); await load(); }
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const renameBundle = async (bundleId: string, name: string) => {
        const id = botId();
        if (!id) return;
        const trimmed = name.trim();
        if (!trimmed) { toast?.error(t('SELLBOT_TOAST_BUNDLE_NAME_REQUIRED')); return; }
        const res = await api.sellbot.renameBundle(id, bundleId, { name: trimmed });
        if (isOk(res.data)) { setBundleNameDrafts(bundleId, trimmed); toast?.info(t('SELLBOT_TOAST_BUNDLE_RENAMED')); await load(); }
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const deleteTier = async (tierId: string) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('SELLBOT_CONFIRM_DELETE_TIER'), true, { confirmLabel: t('COMMON_DELETE'), cancelLabel: t('COMMON_CANCEL') })) return;
        const res = await api.sellbot.deleteTier(id, tierId);
        if (isOk(res.data)) await load();
        else toast?.error(t('SELLBOT_TOAST_DELETE_FAILED'));
    };

    // The editor persists one dimension at a time and asks the server what the draft describes.
    const ruleApi = {
        create: async (rule: ItemMatchRuleRequest) => {
            const id = botId(); const o = offer();
            if (!id || !o) return { ok: false };
            const res = await api.sellbot.createRule(id, o.id, rule);
            return { ok: isOk(res.data), error: firstError(res.data) };
        },
        update: async (ruleId: string, rule: ItemMatchRuleRequest) => {
            const id = botId(); const o = offer();
            if (!id || !o) return { ok: false };
            const res = await api.sellbot.updateRule(id, o.id, ruleId, rule);
            return { ok: isOk(res.data), error: firstError(res.data) };
        },
        remove: async (ruleId: string) => {
            const id = botId(); const o = offer();
            if (!id || !o) return { ok: false };
            const res = await api.sellbot.deleteRule(id, o.id, ruleId);
            return { ok: isOk(res.data), error: firstError(res.data) };
        },
        reload: async () => { await load(); },
        preview: async (request: { itemId: number; metadata: number; rules: ItemMatchRuleRequest[] }) => {
            const id = botId();
            if (!id) return null;
            const res = await api.sellbot.rulePreview(id, request);
            return isOk(res.data) ? res.data.value! : null;
        },
    };

    const openPicker = async () => {
        const id = botId();
        if (!id) return;
        setPickerOpen(true);
        try {
            const storageList = await inv.fetchStorages();
            setStorages(reconcile(storageList, { key: 'id' }));
            if (storageList.length > 0 && !pickStorageId()) choosePickStorage(storageList[0].id);
        } catch (e: any) {
            toast?.error(e?.message ?? t('INVENTORY_CRAFT_STORAGES_FAILED'));
        }
    };

    const choosePickStorage = (storageId: string) => {
        setPickStorageId(storageId);
        void loadPickItems(storageId);
    };

    const loadPickItems = async (storageId: string) => {
        setPickItems([]);
        if (!storageId) return;
        const res = await api.inventory.getStorageItems(storageId);
        if (isOk(res.data)) setPickItems(reconcile(res.data.value.stacks ?? [], { key: 'key' }));
    };

    const filteredPickItems = (): ItemStackDto[] =>
        matchItemsByRelevance(pickQuery(), pickItems, cachedNameOf(itemNameCache, displayName));

    const buildFromItem = async (s: ItemStackDto) => {
        const id = botId(); const o = offer();
        if (!id || !o) return;
        if (o.rules.length > 0 && !await showConfirm(t('SELLBOT_CONFIRM_BUILD_REPLACE'), true, { confirmLabel: t('SELLBOT_BUILD_REPLACE_CONFIRM'), cancelLabel: t('COMMON_CANCEL') })) return;
        const res = await api.sellbot.applyRules(id, o.id, { items: [s], pinned: [] });
        if (isOk(res.data)) {
            toast?.info(t('SELLBOT_TOAST_RULES_BUILT'));
            setPickerOpen(false); setPickQuery('');
            await load();
        } else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const openWizard = async () => {
        const id = botId();
        if (!id) return;
        setWizardOpen(true);
        const [stockRes, customRes] = await Promise.all([api.sellbot.getChamberStock(id), api.inventory.getCustomItems()]);
        if (isOk(stockRes.data)) {
            setWizardStock(reconcile(stockRes.data.value.stacks ?? [], { key: 'key' }));
            setWizardQuantities(reconcile(stockRes.data.value.quantities ?? {}));
        }
        if (isOk(customRes.data)) setWizardCustomItems(reconcile(customRes.data.value.items ?? [], { key: 'hash' }));
    };

        const wizardSource: WizardItemSource = {
        placeholderKey: 'SELLBOT_PICK_PLACEHOLDER_SEARCH',
        search: (query) => searchSellbotItems(query, wizardStock, wizardCustomItems, resolver(), displayName, itemNameCache),
        stockOf: (item) => wizardQuantities[item.key],
    };

    const wizardDiff = async (request: SellbotWizardRequest) => {
        const id = botId();
        if (!id) return { ok: false as const, error: t('SELLBOT_TOAST_UPDATE_FAILED') };
        const res = await api.sellbot.wizardDiff(id, request);
        return isOk(res.data)
            ? { ok: true as const, value: res.data.value! }
            : { ok: false as const, error: firstError(res.data) ?? t('SELLBOT_TOAST_UPDATE_FAILED') };
    };

    const wizardFinish = async (request: SellbotWizardRequest) => {
        const id = botId(); const o = offer();
        if (!id || !o) return { ok: false };
        const res = await api.sellbot.applyRules(id, o.id, request);
        if (!isOk(res.data)) return { ok: false, error: firstError(res.data) ?? t('SELLBOT_TOAST_UPDATE_FAILED'), collision: res.data?.collision };
        toast?.info(t('SELLBOT_TOAST_RULES_BUILT'));
        await load();
        return { ok: true };
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <A href="/sellbot/offers" class="btn btn-secondary"><i class="fa-solid fa-arrow-left" /> <Text val="SELLBOT_BACK_TO_OFFERS" /></A>
            </div>

            <Show when={!loading()} fallback={<div class={styles.skeletonGrid}><For each={[1, 2, 3]}>{() => <div class={styles.skeletonCard} />}</For></div>}>
                <Show when={offer()} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-circle-question" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                        <p><Text val="SELLBOT_OFFER_NOT_FOUND" /></p>
                        <A href="/sellbot/offers" class="btn btn-primary"><Text val="SELLBOT_BACK_TO_OFFERS" /></A>
                    </div>
                }>
                    {(() => { const o = offer()!;
                        const releasedCount = () => o.rules.filter(rule => rule.isEnabled && rule.operator === 'Ignore').length;
                        return (
                        <>
                            <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                                <div class={styles.offerHeader}>
                                    <ItemStackView stack={toView(o.item)} />
                                    <div class="v" style={{ gap: '2px', flex: 1 }}>
                                        <span class={styles.offerName}>{displayName(o.item)}</span>
                                        <span class={styles.helper}>{o.item.key}</span>
                                    </div>
                                    <button type="button" class="btn btn-secondary btn-sm btn-icon"
                                        title={t('SELLBOT_OFFER_COPY_ID')} aria-label={t('SELLBOT_OFFER_COPY_ID')}
                                        onClick={() => void copy(o.id)}>
                                        <i class="fa-solid fa-copy" />
                                    </button>
                                    <span class={`${styles.badge} ${releasedCount() > 0 ? styles.badgeInfo : styles.badgeMuted}`}>{releasedCount() > 0 ? t('SELLBOT_OFFER_RELEASED', releasedCount()) : t('SELLBOT_OFFER_EXACT')}</span>
                                    <span class={styles.stock} title={o.ignoresStockCache ? t('SELLBOT_OFFER_STOCK_FROM_LAST_SCAN_HELP') : undefined}>
                                        <Text val="SELLBOT_STOCK" />: {formatQuantity(o.availableStock, offerStackSize())}<Show when={o.ignoresStockCache}> <i class="fa-solid fa-eye-slash" /></Show>
                                    </span>
                                </div>
                                <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                    <label class="h hcenter" style={{ gap: 'var(--spacing-sm)', flex: 1 }}>
                                        <span class={styles.helper}><Text val="SELLBOT_OFFER_NOTE" /></span>
                                        <input type="text" class={styles.ruleText} value={noteDraft() ?? o.note ?? ''}
                                            placeholder={t('SELLBOT_OFFER_NOTE_PLACEHOLDER')}
                                            onInput={(e) => setNoteDraft(e.currentTarget.value)} />
                                    </label>
                                    <Show when={noteDraft() !== null && noteDraft() !== (o.note ?? '')}>
                                        <button type="button" class="btn btn-sm btn-primary" onClick={() => void saveNote()}><i class="fa-solid fa-check" /> <Text val="SELLBOT_SAVE" /></button>
                                        <button type="button" class="btn btn-sm btn-secondary" onClick={() => setNoteDraft(null)}><Text val="COMMON_CANCEL" /></button>
                                    </Show>
                                </div>
                                <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                    <input type="checkbox" checked={o.ignoresStockCache}
                                        onChange={(e) => void setIgnoresStockCache(e.currentTarget.checked)} />
                                    <span class={styles.statusItemLabel}><Text val="SELLBOT_OFFER_ALWAYS_CHECK_CHAMBER" /></span>
                                </label>
                                <span class={styles.helper}><Text val="SELLBOT_OFFER_ALWAYS_CHECK_CHAMBER_HELP" /></span>
                                <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                    <input type="checkbox" checked={o.isUnlisted}
                                        onChange={(e) => void setUnlisted(e.currentTarget.checked)} />
                                    <span class={styles.statusItemLabel}><Text val="SELLBOT_OFFER_UNLISTED" /></span>
                                </label>
                                <span class={styles.helper}><Text val="SELLBOT_OFFER_UNLISTED_HELP" /></span>
                                <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                    <span class={styles.statusItemLabel}><Text val="SELLBOT_OFFER_LIST_AS_COMPRESSED" /></span>
                                    <select value={o.listAsCompressed == null ? '' : o.listAsCompressed ? 'on' : 'off'}
                                        onChange={(e) => void setListAsCompressed(e.currentTarget.value)}>
                                        <option value="">{t('SELLBOT_OFFER_LIST_AS_COMPRESSED_INHERIT')}</option>
                                        <option value="on">{t('SELLBOT_OFFER_LIST_AS_COMPRESSED_ON')}</option>
                                        <option value="off">{t('SELLBOT_OFFER_LIST_AS_COMPRESSED_OFF')}</option>
                                    </select>
                                </label>
                                <span class={styles.helper}><Text val="SELLBOT_OFFER_LIST_AS_COMPRESSED_HELP" /></span>
                            </div>

                            <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                <div class={styles.sectionHeader}><h2><Text val="SELLBOT_TIERS" /></h2><span class={styles.helper}><Text val="SELLBOT_TIERS_HELP" /></span></div>
                                <div class={styles.tierList} style={{ 'padding-left': '0' }}>
                                    <Show when={o.tiers.length === 0}>
                                        <div class={styles.tierEmpty}><i class="fa-solid fa-tag" /> <Text val="SELLBOT_NO_LISTINGS" /></div>
                                    </Show>
                                    <For each={o.tiers}>{(tr) => (
                                        <Show when={editingTierId() === tr.id} fallback={
                                            <div class={`${styles.tierRow} ${!tr.isEnabled ? styles.paused : ''}`}>
                                                <span class={styles.tierCount}>×{formatQuantity(tr.count, offerStackSize())}</span>
                                                <span class={styles.tierPrice}>{fmtMoney(tr.price)}</span>
                                                <Show when={tr.isEnabled && !o.ignoresStockCache && o.availableStock < tr.count}><span class={`${styles.badge} ${styles.badgeDanger}`}><Text val="SELLBOT_OUT_OF_STOCK" /></span></Show>
                                                <Show when={!tr.isEnabled}><SellbotPausedBadge /></Show>
                                                <Show when={tr.isUnlisted}>
                                                    <span class={`${styles.badge} ${styles.badgeInfo}`} title={t('SELLBOT_TIER_UNLISTED_HELP')}>
                                                        <i class="fa-solid fa-eye-slash" /> <Text val="SELLBOT_TIER_UNLISTED" />
                                                    </span>
                                                </Show>
                                                <Show when={bundleAt(tr.price)}>{(bundle) => (
                                                    <span class={`${styles.badge} ${styles.badgeBundle}`} title={t('SELLBOT_BUNDLE_HELP')}>
                                                        <i class="fa-solid fa-box-open" /> {bundle().name ?? t('SELLBOT_BUNDLE_BADGE')}
                                                    </span>
                                                )}</Show>
                                                <span style={{ flex: 1 }} />
                                                <button type="button" class="btn btn-sm btn-secondary" aria-label={t('SELLBOT_EDIT_TIER')} title={t('SELLBOT_EDIT_TIER')} onClick={() => startEditTier(tr.id, tr.count, tr.price)}><i class="fa-solid fa-pen-to-square" /></button>
                                                <button type="button" class="btn btn-sm btn-secondary" onClick={() => void patchTier(tr, { isEnabled: !tr.isEnabled })}>{t(tr.isEnabled ? 'SELLBOT_PAUSE' : 'SELLBOT_RESUME')}</button>
                                                <button type="button" class="btn btn-sm btn-secondary" title={t('SELLBOT_TIER_UNLISTED_HELP')} onClick={() => void patchTier(tr, { isUnlisted: !tr.isUnlisted })}>{t(tr.isUnlisted ? 'SELLBOT_TIER_LIST' : 'SELLBOT_TIER_UNLIST')}</button>
                                                <button type="button" class="btn btn-sm btn-danger" aria-label={t('COMMON_DELETE')} title={t('COMMON_DELETE')} onClick={() => void deleteTier(tr.id)}><i class="fa-solid fa-trash" /></button>
                                            </div>
                                        }>
                                            <div class={styles.tierAddRow}>
                                                <QuantityInput class={styles.countInput} value={Number(editTierCount() || 0)} onChange={setEditTierCount} stackSize={offerStackSize()} presets={false} placeholder={t('SELLBOT_COUNT')} />
                                                <CurrencyInput class={styles.priceInput} value={editTierPrice()} onChange={setEditTierPrice} aria-label={t('SELLBOT_PRICE')} />
                                                <button type="button" class="btn btn-sm btn-primary" onClick={() => void saveEditTier(tr.id, tr.isEnabled, tr.isUnlisted)}><i class="fa-solid fa-check" /> <Text val="SELLBOT_SAVE" /></button>
                                                <button type="button" class="btn btn-sm btn-secondary" onClick={cancelEditTier}><Text val="COMMON_CANCEL" /></button>
                                            </div>
                                        </Show>
                                    )}</For>
                                    <form class={styles.tierAddRow} onSubmit={(e) => { e.preventDefault(); void addTier(); }}>
                                        <QuantityInput class={styles.countInput} value={Number(tCount() || 0)} onChange={setTCount} stackSize={offerStackSize()} presets={false} placeholder={t('SELLBOT_COUNT')} />
                                        <CurrencyInput class={styles.priceInput} value={tPrice()} onChange={setTPrice} placeholder={t('SELLBOT_PRICE')} />
                                        <button type="submit" class="btn btn-sm btn-secondary"><i class="fa-solid fa-plus" /> <Text val="SELLBOT_ADD_TIER" /></button>
                                    </form>
                                </div>
                            </div>

                            <For each={bundles.filter(b => b.members.some(m => m.offerId === o.id))}>{(bundle) => (
                                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                    <div class={styles.sectionHeader}>
                                        <h2><i class="fa-solid fa-box-open" /> <Text val="SELLBOT_BUNDLE_CARD" /></h2>
                                        <span class={styles.helper}><Text val="SELLBOT_BUNDLE_HELP" /></span>
                                    </div>
                                    <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                        <label class="h hcenter" style={{ gap: 'var(--spacing-sm)', flex: 1 }}>
                                            <span class={styles.helper}><Text val="SELLBOT_BUNDLE_NAME" /></span>
                                            <input type="text" class={styles.ruleText}
                                                value={bundleNameDrafts[bundle.id ?? ''] ?? bundle.name ?? ''}
                                                placeholder={t('SELLBOT_BUNDLE_NAME_PLACEHOLDER')}
                                                onInput={(e) => setBundleNameDrafts(bundle.id ?? '', e.currentTarget.value)} />
                                        </label>
                                        <Show when={bundle.id && (bundleNameDrafts[bundle.id] ?? bundle.name ?? '') !== (bundle.name ?? '')}>
                                            <button type="button" class="btn btn-sm btn-primary" title={t('SELLBOT_BUNDLE_RENAME')}
                                                onClick={() => void renameBundle(bundle.id!, bundleNameDrafts[bundle.id!] ?? '')}>
                                                <i class="fa-solid fa-check" /> <Text val="SELLBOT_SAVE" />
                                            </button>
                                            <button type="button" class="btn btn-sm btn-secondary"
                                                onClick={() => setBundleNameDrafts(bundle.id!, bundle.name ?? '')}><Text val="COMMON_CANCEL" /></button>
                                        </Show>
                                    </div>
                                    <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                        <span class={styles.helper}><Text val="SELLBOT_BUNDLE_PRICE" /></span>
                                        <span class={styles.tierPrice}>{fmtMoney(bundle.price)}</span>
                                        <span style={{ flex: 1 }} />
                                        <span class={styles.stock}>{t('SELLBOT_BUNDLE_AVAILABLE', formatQuantity(bundle.buyableBundles))}</span>
                                    </div>
                                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <span class={styles.helper}><Text val="SELLBOT_BUNDLE_CONTAINS" /></span>
                                        <For each={bundle.members}>{(member) => (
                                            <div class={styles.bundleMember}>
                                                <span class={styles.tierCount}>×{formatQuantity(member.count, siblingStackSize(member.offerId))}</span>
                                                <span class={styles.bundleMemberName}>{siblingName(member.offerId)}</span>
                                                <Show when={!member.isEnabled}><SellbotPausedBadge /></Show>
                                                <Show when={member.offerId !== o.id}>
                                                    <A href={`/sellbot/offer/${member.offerId}`} class="btn btn-sm btn-secondary">
                                                        <i class="fa-solid fa-sliders" /> <Text val="SELLBOT_DETAILS" />
                                                    </A>
                                                </Show>
                                            </div>
                                        )}</For>
                                    </div>
                                </div>
                            )}</For>

                            <Show when={botId()} keyed>{(bid) => (
                                <SellbotLimitsEditor botId={bid} offerId={o.id} stackSize={offerStackSize()} kind="Verkauf" limits={o.verkaufLimits} onChanged={load} />
                            )}</Show>

                            <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                <div class={styles.sectionHeader}><h2><Text val="SELLBOT_ANKAUF_STAGES" /></h2><span class={styles.helper}><Text val="SELLBOT_ANKAUF_STAGES_HELP" /></span></div>
                                <Show when={o.ankaufTier} keyed fallback={
                                    <div class="h hcenter" style={{ 'justify-content': 'space-between' }}>
                                        <div class={styles.tierEmpty}><i class="fa-solid fa-circle-info" /> <Text val="SELLBOT_ANKAUF_NO_STAGES" /></div>
                                        <button type="button" class="btn btn-sm btn-primary" onClick={() => void enableAnkauf()}><i class="fa-solid fa-plus" /> <Text val="SELLBOT_ANKAUF_ENABLE" /></button>
                                    </div>
                                }>
                                    {(at) => (
                                        <div class={styles.tierList} style={{ 'padding-left': '0' }}>
                                            <div class={`h hcenter ${styles.tierRow} ${!at.isEnabled ? styles.paused : ''}`} style={{ gap: 'var(--spacing-sm)' }}>
                                                <label class="h hcenter" style={{ gap: 'var(--spacing-xs)' }}>
                                                    <span class={styles.helper}><Text val="SELLBOT_ANKAUF_DESTINATION_OVERRIDE" /></span>
                                                    <select value={at.destinationOverride ?? ''} onChange={(e) => void changeAnkaufDestinationOverride(e.currentTarget.value)}>
                                                        <For each={ANKAUF_DESTINATION_OVERRIDES}>{(d) => <option value={d}>{t(d === '' ? 'SELLBOT_DESTINATION_DEFAULT' : 'SELLBOT_DESTINATION_' + d.toUpperCase())}</option>}</For>
                                                    </select>
                                                </label>
                                                <span style={{ flex: 1 }} />
                                                <Show when={!at.isEnabled}><SellbotPausedBadge /></Show>
                                                <button type="button" class="btn btn-sm btn-secondary" onClick={() => void toggleAnkaufTier()}>{t(at.isEnabled ? 'SELLBOT_PAUSE' : 'SELLBOT_RESUME')}</button>
                                                <button type="button" class="btn btn-sm btn-danger" aria-label={t('COMMON_DELETE')} title={t('COMMON_DELETE')} onClick={() => void deleteAnkaufTier()}><i class="fa-solid fa-trash" /></button>
                                            </div>
                                            <Show when={at.stages.length === 0}>
                                                <div class={styles.tierEmpty}><i class="fa-solid fa-layer-group" /> <Text val="SELLBOT_ANKAUF_NO_STAGES" /></div>
                                            </Show>
                                            <For each={[...at.stages].sort((a, b) => b.count - a.count)}>{(stage) => (
                                                <Show when={editingStageId() === stage.id} fallback={
                                                    <div class={`${styles.tierRow} ${!stage.isEnabled ? styles.paused : ''}`}>
                                                        <span class={styles.tierCount}>×{formatQuantity(stage.count, offerStackSize())}</span>
                                                        <span class={styles.tierPrice}>{fmtMoney(stage.price)}</span>
                                                        <Show when={!stage.isEnabled}><SellbotPausedBadge /></Show>
                                                        <span style={{ flex: 1 }} />
                                                        <button type="button" class="btn btn-sm btn-secondary" aria-label={t('SELLBOT_EDIT_STAGE')} title={t('SELLBOT_EDIT_STAGE')} onClick={() => startEditStage(stage.id, stage.count, stage.price)}><i class="fa-solid fa-pen-to-square" /></button>
                                                        <button type="button" class="btn btn-sm btn-secondary" onClick={() => void toggleStage(stage.id, stage.isEnabled, stage.count, stage.price)}>{t(stage.isEnabled ? 'SELLBOT_PAUSE' : 'SELLBOT_RESUME')}</button>
                                                        <button type="button" class="btn btn-sm btn-danger" aria-label={t('COMMON_DELETE')} title={t('COMMON_DELETE')} onClick={() => void deleteStage(stage.id)}><i class="fa-solid fa-trash" /></button>
                                                    </div>
                                                }>
                                                    <div class={styles.tierAddRow}>
                                                        <QuantityInput class={styles.countInput} value={Number(editStageCount() || 0)} onChange={setEditStageCount} stackSize={offerStackSize()} presets={false} placeholder={t('SELLBOT_COUNT')} />
                                                        <CurrencyInput class={styles.priceInput} value={editStagePrice()} onChange={setEditStagePrice} aria-label={t('SELLBOT_PRICE')} />
                                                        <button type="button" class="btn btn-sm btn-primary" onClick={() => void saveEditStage(stage.id, stage.isEnabled)}><i class="fa-solid fa-check" /> <Text val="SELLBOT_SAVE" /></button>
                                                        <button type="button" class="btn btn-sm btn-secondary" onClick={cancelEditStage}><Text val="COMMON_CANCEL" /></button>
                                                    </div>
                                                </Show>
                                            )}</For>
                                            <form class={styles.tierAddRow} onSubmit={(e) => { e.preventDefault(); void addStage(); }}>
                                                <QuantityInput class={styles.countInput} value={Number(stageCount() || 0)} onChange={setStageCount} stackSize={offerStackSize()} presets={false} placeholder={t('SELLBOT_COUNT')} />
                                                <CurrencyInput class={styles.priceInput} value={stagePrice()} onChange={setStagePrice} placeholder={t('SELLBOT_PRICE')} />
                                                <button type="submit" class="btn btn-sm btn-secondary"><i class="fa-solid fa-plus" /> <Text val="SELLBOT_ADD_ANKAUF_STAGE" /></button>
                                            </form>
                                        </div>
                                    )}
                                </Show>
                            </div>

                            <Show when={o.ankaufTier}>
                                <Show when={botId()} keyed>{(bid) => (
                                    <SellbotLimitsEditor botId={bid} offerId={o.id} stackSize={offerStackSize()} kind="Ankauf" limits={o.ankaufLimits} onChanged={load} />
                                )}</Show>
                            </Show>

                            <ItemIdentityEditor
                                itemId={o.item.itemId}
                                metadata={o.baseMetadata}
                                snbt={o.item.snbt}
                                rules={o.rules}
                                dimensions={o.dimensions}
                                ruleApi={ruleApi}
                                headerAction={
                                    <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                                        <button type="button" class="btn btn-sm btn-secondary" onClick={() => void openWizard()}><i class="fa-solid fa-list-check" /> <Text val="SELLBOT_OFFER_WIZARD_START" /></button>
                                        <button type="button" class="btn btn-sm btn-primary" onClick={() => void openPicker()}><i class="fa-solid fa-wand-magic-sparkles" /> <Text val="SELLBOT_BUILD_FROM_ITEM" /></button>
                                    </div>
                                }
                            />

                            <ItemIdentityWizard open={wizardOpen()} onClose={() => setWizardOpen(false)}
                                source={wizardSource} diff={wizardDiff} onFinish={wizardFinish}
                                collisionHref={(entityId) => `/sellbot/offer/${entityId}`} />
                        </>
                    ); })()}
                </Show>
            </Show>

            <Modal open={pickerOpen()} onClose={() => setPickerOpen(false)} title={<Text val="SELLBOT_BUILD_FROM_ITEM" />} subtitle={<Text val="SELLBOT_BUILD_FROM_ITEM_HINT" />}>
                <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.statLabel}><Text val="SELLBOT_STORAGE" /></span>
                        <StorageSelect storages={storages} value={pickStorageId()} onChange={choosePickStorage} />
                    </label>
                    <input type="text" placeholder={t('SELLBOT_PICK_PLACEHOLDER_SEARCH')} value={pickQuery()} onInput={(e) => setPickQuery(e.currentTarget.value)} />
                    <div class={styles.pickerList} style={{ position: 'static', 'max-height': '24rem' }}>
                        <Show when={filteredPickItems().length > 0} fallback={<div class={styles.pickerEmpty}><Text val="SELLBOT_PICKER_NO_MATCH" /></div>}>
                            <For each={filteredPickItems()}>{(s) => (
                                <button type="button" class={styles.pickerItem} onClick={() => void buildFromItem(s)}>
                                    <ItemStackView stack={toView(s)} />
                                    <span>{displayName(s)}</span>
                                </button>
                            )}</For>
                        </Show>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
